/**
 * PartnerPitchPage - Custom Partner/Sponsor Pitch Pages
 * 
 * Editable by hosts with access control from super admin
 * Dynamic content based on partner type (hilton, sponsor, etc.)
 * NO live stats - uses curated value propositions
 */

import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import PhygitalIcon from "@/components/PhygitalIcon";
import {
  ArrowRight, Calendar, MapPin, Users, Building2, Crown, 
  CheckCircle, Star, Mail, Sparkles, Globe, Heart,
  Palette, Zap, Handshake, Camera, PartyPopper, Utensils, Accessibility,
  Link2, BarChart3
} from "lucide-react";


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hilton-specific content from email conversations with Babette
const HILTON_CONTENT = {
  hero: {
    title: "High Tech High Fashion",
    subtitle: "An LA Fashion Week Kickoff Experience",
    description: "The evening brings together fashion, technology, culture, and capital in an intimate setting, with a panel conversation as part of the program.",
    date: "March 11, 2026",
    location: "Hilton Woodland Hills",
  },
  about: {
    headline: "Our Partnership Proposal",
    description: `Our vision is to position the hotel as an active sponsor and co-host, using this moment to showcase what future LA Fashion Week activations can look like at the property, while meaningfully connecting the hotel to LA Fashion Week curators, tastemakers, and key stakeholders.`,
    hotelFit: `The hotel's fashion-forward design, from the runway-inspired lobby installations to the restaurant appropriately named Runway, makes it a natural setting for a collaboration that introduces LA's fashion tastemakers, technologists, and creative leaders to the property in an elevated, experiential way.`,
    opportunity: `This is a strong opportunity to position Hilton as a cultural hub and future LA Fashion Week destination.`
  },
  proposal: {
    items: [
      { title: "Event Space", description: "Hotel sponsorship of the event space" },
      { title: "Hosted Open Bar", description: "Signature cocktails and beverages" },
      { title: "Heavy Hors d'oeuvres", description: "Curated culinary experience" },
      { title: "Collaborative Branding", description: "Positioning as a future LA Fashion Week destination" }
    ]
  },
  valueAdd: {
    headline: "Technology-Driven Value",
    features: [
      {
        title: "Book Your Stay Link Tracking",
        description: "Trackable links for direct bookings, measuring event ROI",
        icon: Building2
      },
      {
        title: "Pre-Arrival Guest Insights",
        description: "Dietary needs and accessibility requirements shared before guests arrive",
        icon: Utensils
      },
      {
        title: "Easy Accessibility Sharing",
        description: "Guests share their needs through our preference card system",
        icon: Accessibility
      }
    ]
  },
  event: {
    description: `An exclusive mixer and fashion tech exhibit where designers and visionaries come together. The evening brings together fashion, technology, culture, and capital in an intimate setting, with a panel conversation as part of the program.`,
    cards: [
      {
        title: "Curated Mixer",
        description: "An intimate gathering of fashion designers, technologists, investors, and creative leaders",
        icon: Users
      },
      {
        title: "Creative + Capital Exchange",
        description: "Meaningful connections between visionaries and investors in fashion-tech",
        icon: Sparkles
      },
      {
        title: "Panel Conversation",
        description: "Insights from industry leaders on the future of fashion and technology",
        icon: Star
      }
    ]
  },
  vision: {
    headline: "Beyond One Event",
    points: [
      "Currently in conversations with the founder of LA Fashion Week for panelist participation",
      "Hilton's flexibility as a privately owned property positions it uniquely for fashion-forward moments",
      "Potential start of a broader relationship with LA Fashion Week and beyond",
      "Part of a series of fashion x tech experiences planned throughout the year across LA and other cities",
      "Leading toward an AI-focused fashion show later this fall for LAFW in October"
    ]
  },
  goal: {
    headline: "Our Goal",
    description: `To create a high-touch, curated experience that demonstrates the hotel's potential to support fashion-forward programming, ultimately driving future bookings and longer-term alignment with LA Fashion Week and its ecosystem.`
  },
  audience: {
    headline: "Who's Attending",
    types: [
      { label: "Fashion Designers", icon: Palette },
      { label: "Tech Innovators", icon: Zap },
      { label: "Investors & VCs", icon: Star },
      { label: "Creative Directors", icon: Camera },
      { label: "Industry Leaders", icon: Crown },
      { label: "Media & Press", icon: Globe }
    ]
  }
};

// Generic sponsor content
const SPONSOR_CONTENT = {
  hero: {
    title: "Partnership Opportunity",
    subtitle: "Join Us in Creating Memorable Experiences",
    tagline: "Connect with an Engaged, Curated Audience"
  },
  tiers: [
    {
      name: "Bronze Partner",
      price: "$1,000",
      benefits: ["Logo on event page", "Social media mentions", "Email inclusion", "Event day signage"]
    },
    {
      name: "Silver Partner",
      price: "$3,000",
      benefits: ["All Bronze benefits", "Branded gift bag item", "Post-event report", "Guest list access"]
    },
    {
      name: "Gold Partner",
      price: "$7,500",
      benefits: ["All Silver benefits", "Speaking opportunity", "VIP dinner access", "Custom activation space"]
    },
    {
      name: "Title Sponsor",
      price: "$15,000+",
      benefits: ["All Gold benefits", "Naming rights", "Full brand integration", "Strategic partnership development"]
    }
  ]
};

export default function PartnerPitchPage() {
  const { eventSlug, partnerType } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const isHilton = partnerType === "hilton";

  useEffect(() => {
    fetchEventData();
  }, [eventSlug]);

  const fetchEventData = async () => {
    try {
      let res = await fetch(`${API}/events/slug/${eventSlug}`);
      if (!res.ok) {
        res = await fetch(`${API}/events/${eventSlug}`);
      }
      if (res.ok) {
        const data = await res.json();
        setEvent(data);
      }
    } catch (err) {
      console.error("Failed to fetch event:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleContact = () => {
    const subject = encodeURIComponent(`Partnership Inquiry: ${event?.title || 'High Tech High Fashion'}`);
    const body = encodeURIComponent(`Hi,\n\nI'm interested in discussing partnership opportunities for ${event?.title || 'High Tech High Fashion'}.\n\nPlease let me know your availability for a conversation.\n\nBest regards`);
    window.location.href = `mailto:partnerships@phygital.com?subject=${subject}&body=${body}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-gray-200 border-t-purple-600 rounded-full animate-spin" />
      </div>
    );
  }

  // Helper to get image URL
  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    // Path includes /api/ prefix, use BACKEND_URL
    const baseUrl = process.env.REACT_APP_BACKEND_URL;
    return `${baseUrl}${path}`;
  };

  // Render Hilton-specific page
  if (isHilton) {
    const coverImage = getImageUrl(event?.cover_image);
    
    return (
      <div className="min-h-screen bg-white">
        {/* Hero Section with Cover Image */}
        <div className="relative text-white overflow-hidden" style={{ backgroundColor: '#002f61' }}>
          {/* Cover Image Background */}
          {coverImage && (
            <div className="absolute inset-0">
              <img 
                src={coverImage} 
                alt="Event Cover" 
                className="w-full h-full object-cover opacity-20"
              />
              <div className="absolute inset-0" style={{ backgroundColor: 'rgba(0, 47, 97, 0.9)' }} />
            </div>
          )}
          
          <div className="max-w-5xl mx-auto px-6 py-16 relative">
            <div className="flex items-center gap-4 mb-6">
              {/* Event Logo/Cover */}
              {coverImage && (
                <div className="w-20 h-20 rounded-xl overflow-hidden bg-white/10 backdrop-blur-sm border border-white/20">
                  <img src={coverImage} alt="HTHF" className="w-full h-full object-cover" />
                </div>
              )}
              <div>
                <Badge className="bg-white/20 text-white border-0 mb-1">Venue Partnership</Badge>
                <p className="text-sm opacity-70">Hilton Woodland Hills</p>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-5xl font-bold mb-3">{HILTON_CONTENT.hero.title}</h1>
            <p className="text-xl text-blue-100 mb-4">{HILTON_CONTENT.hero.subtitle}</p>
            <p className="text-blue-100 mb-6 max-w-2xl">{HILTON_CONTENT.hero.description}</p>
            
            <div className="flex flex-wrap items-center gap-4 text-sm text-blue-100 mb-8">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>{HILTON_CONTENT.hero.date}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                <span>{HILTON_CONTENT.hero.location}</span>
              </div>
            </div>
          </div>
        </div>

        {/* About Section - Our Partnership Proposal */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-4">{HILTON_CONTENT.about.headline}</h2>
            <p className="text-gray-600 text-lg mb-4">{HILTON_CONTENT.about.description}</p>
            <p className="text-gray-500 mb-4">{HILTON_CONTENT.about.hotelFit}</p>
            <p className="text-purple-600 font-medium text-lg">{HILTON_CONTENT.about.opportunity}</p>
          </div>
        </section>

        {/* Proposal Items - 4 cards only */}
        <section className="py-16 px-6 bg-blue-50">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-4">
              {HILTON_CONTENT.proposal.items.map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-white rounded-lg">
                  <CheckCircle className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">{item.title}</p>
                    <p className="text-sm text-gray-500">{item.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Technology Value Add */}
        <section className="py-16 px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-3">{HILTON_CONTENT.valueAdd.headline}</h2>
            <p className="text-gray-500 mb-8">How Phygital technology adds value to the partnership</p>
            
            <div className="grid md:grid-cols-3 gap-6">
              {HILTON_CONTENT.valueAdd.features.map((feature, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="pt-6">
                    <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center mb-4">
                      <feature.icon className="w-6 h-6 text-purple-600" />
                    </div>
                    <h3 className="font-bold mb-2">{feature.title}</h3>
                    <p className="text-sm text-gray-600">{feature.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Who's Attending */}
        <section className="py-16 px-6 bg-gray-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold mb-8 text-center">{HILTON_CONTENT.audience.headline}</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {HILTON_CONTENT.audience.types.map((type, idx) => (
                <div key={idx} className="flex items-center gap-3 p-4 border rounded-lg bg-white">
                  <type.icon className="w-5 h-5 text-purple-600" />
                  <span className="font-medium">{type.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Goal Section */}
        <section className="py-16 px-6 bg-purple-50">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-2xl font-bold mb-4">{HILTON_CONTENT.goal.headline}</h2>
            <p className="text-lg text-gray-600">{HILTON_CONTENT.goal.description}</p>
          </div>
        </section>

        {/* Simple Footer with Event Link */}
        <section className="py-8 px-6 bg-gray-100">
          <div className="max-w-2xl mx-auto text-center">
            <Button variant="outline" onClick={() => window.open('https://datadreaming.cloud/hthf', '_blank')}>
              View Event Page
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </section>

        {/* Footer */}
        <footer className="py-6 border-t">
          <div className="max-w-6xl mx-auto px-6 flex items-center justify-center gap-2 text-sm text-gray-500">
            <PhygitalIcon className="w-5 h-5" />
            <span>Powered by Phygital</span>
          </div>
        </footer>
      </div>
    );
  }

  // Render Generic Sponsor page
  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <div className="bg-gradient-to-br from-green-500 to-emerald-700 text-white py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Badge className="bg-white/20 border-0 mb-4">Sponsorship Opportunity</Badge>
          <h1 className="text-4xl font-bold mb-3">{event?.title || SPONSOR_CONTENT.hero.title}</h1>
          <p className="text-xl text-green-100 mb-6">{SPONSOR_CONTENT.hero.tagline}</p>
          <Button size="lg" className="bg-white text-green-700 hover:bg-green-50" onClick={handleContact}>
            <Handshake className="w-5 h-5 mr-2" />
            Discuss Partnership
          </Button>
        </div>
      </div>

      {/* Tiers */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-10">Partnership Tiers</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {SPONSOR_CONTENT.tiers.map((tier, idx) => (
              <Card key={idx} className={`border-2 ${idx === 3 ? 'border-green-400 bg-green-50' : ''}`}>
                <CardHeader className="text-center">
                  <CardTitle>{tier.name}</CardTitle>
                  <p className="text-2xl font-bold text-green-600">{tier.price}</p>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {tier.benefits.map((b, bIdx) => (
                      <li key={bIdx} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5" />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl font-bold mb-4">Ready to Partner?</h2>
          <p className="text-gray-600 mb-8">Let's discuss how we can create value together.</p>
          <Button size="lg" onClick={handleContact}>
            <Mail className="w-5 h-5 mr-2" />
            Contact Us
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 border-t">
        <div className="flex items-center justify-center gap-2 text-sm text-gray-500">
          <PhygitalIcon className="w-5 h-5" />
          <span>Powered by Phygital</span>
        </div>
      </footer>
    </div>
  );
}
